import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  icon: React.ComponentType<any>;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon: Icon, color }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  const getTrendIcon = () => {
    if (!change) return null;
    
    switch (change.type) {
      case 'increase':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'decrease':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <Minus className="w-4 h-4 text-gray-600" />;
    }
  };

  const getChangeColor = () => {
    if (!change) return '';
    
    switch (change.type) {
      case 'increase':
        return 'text-green-600';
      case 'decrease':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div 
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer group animate-slide-up"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 transition-colors duration-200">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 transition-all duration-300 group-hover:scale-110">{value}</p>
          {change && (
            <div className={`flex items-center space-x-1 mt-2 ${getChangeColor()} transition-all duration-200 ${isHovered ? 'animate-bounce-subtle' : ''}`}>
              {getTrendIcon()}
              <span className="text-sm font-medium">
                {change.value > 0 ? '+' : ''}{change.value}%
              </span>
              <span className="text-xs text-gray-500">ce mois</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color} shadow-lg transition-all duration-300 group-hover:shadow-xl group-hover:scale-110 group-hover:rotate-3`}>
          <Icon className={`w-6 h-6 text-white transition-all duration-300 ${isHovered ? 'animate-pulse' : ''}`} />
        </div>
      </div>
    </div>
  );
};

interface DashboardStatsProps {
  stats: Array<{
    title: string;
    value: string | number;
    change?: {
      value: number;
      type: 'increase' | 'decrease' | 'neutral';
    };
    icon: React.ComponentType<any>;
    color: string;
  }>;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <StatCard key={index} {...stat} />
      ))}
    </div>
  );
};